import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { QueryKey } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { updateGuild } from 'app/frontend/base/api/guild'
import { useGuild } from 'app/frontend/base/config/context/guild'
import { useLogo } from 'app/frontend/base/hooks/useLogo'
import { useRefreshData } from 'app/frontend/base/hooks/useRefreshData'
import Timestamp from 'app/frontend/base/components/Timestamp'

interface Props {
  label?: string
  layout?: 'horizontal' | 'vertical'
  successKey?: string
  callback?: () => void
  queryToInvalidate?: QueryKey
  className?: string
}

const GuildDataRefreshButton = ({
  label = 'shared.update-now',
  layout = 'vertical',
  successKey,
  callback,
  queryToInvalidate,
  className,
}: Props) => {
  const { t } = useTranslation()
  const { guild, guildReference } = useGuild()
  const { Logo } = useLogo()

  const { refreshing, refreshData } = useRefreshData({
    retriever: updateGuild,
    id: guildReference?.id,
    successKey,
    callback,
    queryToInvalidate,
  })

  const button = (
    <Button
      variant='outline'
      size='sm'
      loading={refreshing}
      spinner='blizzard'
      className='flex items-center gap-3 min-w-38'
      disabled={!guildReference?.id}
      onClick={() => refreshData()}
    >
      <Logo className='w-6 h-6' />
      {t(label)}
    </Button>
  )

  const timestamp = (
    <div className='flex gap-1 text-xs text-right'>
      <span>{t('shared.last-updated')}</span>
      <Timestamp time={guild?.characterDataRefreshedAt} colorNever={false} />
    </div>
  )

  return (
    <div
      className={cn(
        'text-sm flex gap-2 font-normal',
        layout === 'vertical'
          ? 'flex-col items-end'
          : 'max-sm:flex-col-reverse items-center',
        className,
      )}
    >
      {layout === 'vertical' ? (
        <>
          {button}
          {timestamp}
        </>
      ) : (
        <>
          {timestamp}
          {button}
        </>
      )}
    </div>
  )
}

export default GuildDataRefreshButton
